import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import isEmpty from "is-empty";

import { TxSelectors } from "src/redux/Tx/selectors";
import { TxActions } from "src/redux/Tx/actions";

import CollapsibleMenu from "src/components/common/CollapsibleMenu/index";
import RadioButtons from "src/components/common/RadioButton/index";
import Dropdown from "src/components/common/Dropdown/index";
import Maturity from "src/components/views/PreviewActions/Maturity";
import ReferenceRate from "src/components/views/PreviewActions/ReferenceRate";
import TradeObjective from "src/components/views/PreviewActions/TradeObjective";
import { isDivLiabilityValid } from "src/utils/validators";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import { LOAN_TYPES } from "src/redux/Tx/constants";

import styles from "./styles.module.scss";

const TX_TYPE_OPTIONS = [
    { label: "Regular", value: LOAN_TYPES.LOAN },
    { label: "EA", value: LOAN_TYPES.HOLD },
];

const CALLABLE_OPTIONS = [
    { label: "List Default", value: "" },
    { label: "Non-Call (N)", value: false },
    { label: "Call (Y)", value: true },
];

const COLLAT_OPTIONS = [
    { label: "List Default", value: "" },
    { label: "Cash", value: "CA" },
    { label: "Non Cash", value: "NC" },
    { label: "Cash Pool", value: "CP" },
];

const CURRENCY_OPTIONS = [
    { label: "List Default", value: "" },
    { label: "EUR", value: "EUR" },
    { label: "GBP", value: "GBP" },
    { label: "JPY", value: "JPY" },
    { label: "KRW", value: "KRW" },
    { label: "TWD", value: "TWD" },
    { label: "USD", value: "USD" },
];

const OFFER_DETAILS_INIT = {
    Callable: "",
    CollateralType: "",
    CollateralCCY: "",
    Dividend: "",
};

export default () => {
    const dispatch = useDispatch();
    const offerSettings = useSelector(TxSelectors.TxInitOfferSettings);
    // Lines below treat entries as cpty OBJECTS (`cpty?.partner`), so
    // use the resolved-object selector, not the raw id-array one. They
    // happened to coexist before because SelectOneAndOnlyCpty (the only
    // path this component is ever fed by) wrote objects into the state;
    // now that the action stores ids consistently with the other
    // SelectXxxCpty actions, this consumer needs the resolving selector.
    const selectedCptys = useSelector(TxSelectors.TxInitSelectedCptys);
    const hasAccess = useSelector(TxSelectors.HasMarkitAccess);
    const {
        "enable-90-day-max-maturity-date": enable90DayMaxMaturity,
        // Spec §3 "Loan Type = EA: Disabled". Same filter as
        // RequestDefaults — keep the radio so the surface shape is
        // unchanged, but only the Loan option is available.
        "limited-ui": limitedUi,
    } = useSelector(SettingsSelectors.FeatureFlags);
    const txTypeOptions = limitedUi
        ? TX_TYPE_OPTIONS.filter((o) => o.value === LOAN_TYPES.LOAN)
        : TX_TYPE_OPTIONS;

    const [tempOfferDetails, setTempOfferDetails] =
        useState(OFFER_DETAILS_INIT);

    // settings applied to every row at clicking "Apply" button
    const handleTempOfferDetailsChange = (field, value) => {
        if (field === "Dividend") {
            if (isNaN(value)) return;
        }

        setTempOfferDetails({
            ...tempOfferDetails,
            [field]: value,
        });
    };

    // settings applied at seding the request
    const updateSettings = (field) => (value) =>
        dispatch(
            TxActions.SetOfferSettings({ ...offerSettings, [field]: value })
        );

    const isDivValid =
        isEmpty(tempOfferDetails.Dividend) ||
        isDivLiabilityValid(tempOfferDetails.Dividend);

    return (
        <React.Fragment>
            <CollapsibleMenu
                title="Loan / Exclusive Arrangement"
                dataTestId="tx-offer-type"
            >
                <Section className={styles.tuplet}>
                    <RadioButtons
                        options={txTypeOptions}
                        dataTestId="tx-offer-select-type"
                        value={offerSettings.type}
                        setter={updateSettings("type")}
                        checkIsDisabled={(opt) =>
                            opt.label == "EA" &&
                            selectedCptys?.length > 0 &&
                            selectedCptys.some((cpty) => cpty?.partner)
                        }
                    />
                </Section>
            </CollapsibleMenu>

            <TradeObjective
                apply={updateSettings("objective")}
                value={offerSettings.objective ?? ""}
            />

            <ReferenceRate
                hasAccess={hasAccess}
                apply={(value) => dispatch(TxActions.ApplyBenchmarkFee(value))}
            />

            <Maturity
                onChange={(value) =>
                    dispatch(
                        TxActions.ApplyMaturity(value, false) // 90 day KR maturity limit is borrower side feature
                    )
                }
            />

            <CollapsibleMenu
                title="Offer Details"
                dataTestId="tx-offer-defaults"
                allowsOverflow
            >
                <Section className={styles.triplet}>
                    <div className={styles.title}>Callable</div>
                    <RadioButtons
                        options={CALLABLE_OPTIONS}
                        dataTestId="tx-offer-select-callable"
                        value={tempOfferDetails.Callable}
                        setter={(val) =>
                            handleTempOfferDetailsChange("Callable", val)
                        }
                    />
                </Section>

                <div className={styles.splitter} />

                <Section className={styles.tuplet}>
                    <div className={styles.title}>Collateral Type</div>
                    <RadioButtons
                        options={COLLAT_OPTIONS}
                        dataTestId="tx-offer-select-collat-type"
                        value={tempOfferDetails.CollateralType}
                        setter={(val) =>
                            handleTempOfferDetailsChange("CollateralType", val)
                        }
                    />
                </Section>

                <div className={styles.splitter} />

                <Section>
                    <div className={styles.title}>Collateral Currency</div>
                    <Dropdown
                        mainStyle="thick-black"
                        value={CURRENCY_OPTIONS.find(
                            (i) => i.value === tempOfferDetails.CollateralCCY
                        )}
                        options={CURRENCY_OPTIONS}
                        placeholder={"(Current)"}
                        onChange={(e) =>
                            handleTempOfferDetailsChange(
                                "CollateralCCY",
                                e.value
                            )
                        }
                        dataTestId="tx-offer-select-collat-ccy"
                        ariaLabel="Select offer"
                    />
                </Section>

                <div className={styles.splitter} />

                <Section className={styles.dividendSection}>
                    <label
                        className={`${styles.dividend} ${
                            !isDivValid ? styles.error : ""
                        }`}
                    >
                        <span>Div Liability</span>
                        <span>=</span>
                        <input
                            data-testid="tx-offer-div"
                            value={tempOfferDetails.Dividend}
                            placeholder="List Default"
                            onChange={(e) =>
                                handleTempOfferDetailsChange(
                                    "Dividend",
                                    e.target.value
                                )
                            }
                        />
                    </label>
                    <div className={styles.percent} />

                    {!isDivValid && (
                        <div
                            data-testid="tx-offer-div-error"
                            className={styles.errorMessage}
                        >
                            <span>Div liability must be below 100.</span>
                        </div>
                    )}
                </Section>
                <Section className={styles.applyOfferDetails}>
                    <button
                        data-testid="apply-tx-offer-defaults"
                        onClick={() =>
                            dispatch(
                                TxActions.ApplySendOfferDetails(
                                    tempOfferDetails
                                )
                            )
                        }
                        disabled={!isDivValid}
                    >
                        Apply
                    </button>
                </Section>
            </CollapsibleMenu>
        </React.Fragment>
    );
};

const Section = ({ className, children }) => (
    <div className={[styles.section].concat(className || []).join(" ")}>
        <div className={styles.input}>{children}</div>
    </div>
);
